/* eslint-disable @typescript-eslint/no-explicit-any */
import type { components, paths } from './api'

/** ===== Types issus d'OpenAPI (déjà présents dans api.d.ts) ===== */
export type PaymentMethod = components['schemas']['PaymentMethod']
export type OverpayPolicy =
  components['schemas']['CreateBillDto']['initialOverpayPolicy']
export type Bill = components['schemas']['BillViewDto']
export type BillsList = components['schemas']['ListBillsResponseDto']
export type BillsSummary = components['schemas']['BillsSummaryResponseDto']
export type BillsAging = components['schemas']['BillsAgingResponseDto']
export type BillPayment = components['schemas']['BillPaymentViewDto']
export type ReceivablesEom = components['schemas']['ReceivablesEomResponseDto']
export type ReceivablesMonthly =
  components['schemas']['ReceivablesMonthlyResponseDto']
export type CustomerMini = components['schemas']['BillCustomerViewDto']
export type Customer = components['schemas']['CustomerViewDto']
export type CustomersList = components['schemas']['ListCustomersResponseDto']
export type CustomerBalance =
  components['schemas']['CustomerBalanceResponseDto']
export type CustomerAccount =
  components['schemas']['CustomerAccountResponseDto']
export type Category = components['schemas']['CategoryViewDto']
export type CategoryList = components['schemas']['CategoryListResponseDto']
export type CategorySelect = components['schemas']['CategorySelectDto']
export type Item = components['schemas']['ItemViewDto']
export type ItemList = components['schemas']['ItemListResponseDto']
export type ItemMoves = components['schemas']['ItemMovesResponseDto']

// === Request bodies
export type LoginBody =
  paths['/auth/login']['post']['requestBody']['content']['application/json']
export type RegisterBody =
  paths['/auth/register']['post']['requestBody']['content']['application/json']
export type ChangePasswordBody =
  paths['/auth/change-password']['patch']['requestBody']['content']['application/json']
export type ForgotPasswordBody =
  paths['/auth/forgot-password']['post']['requestBody']['content']['application/json']
export type ResetPasswordBody =
  paths['/auth/reset-password']['post']['requestBody']['content']['application/json']
export type VerifyEmailBody =
  paths['/auth/verify-email']['post']['requestBody']['content']['application/json']
export type ResendVerificationBody =
  paths['/auth/resend-verification']['post']['requestBody']['content']['application/json']

export type CreateBillBody =
  paths['/bills']['post']['requestBody']['content']['application/json']
export type CreateCategoryBody =
  paths['/categories']['post']['requestBody']['content']['application/json']
export type UpdateCategoryBody =
  paths['/categories/{id}']['patch']['requestBody']['content']['application/json']
export type CreateItemBody =
  paths['/items']['post']['requestBody']['content']['application/json']
export type UpdateItemBody =
  paths['/items/{id}']['patch']['requestBody']['content']['application/json']
export type PurchaseBody =
  paths['/items/_purchase']['post']['requestBody']['content']['application/json']
export type AdjustBody =
  paths['/items/_adjust']['post']['requestBody']['content']['application/json']
export type CreateCustomerBody =
  paths['/customers']['post']['requestBody']['content']['application/json']
export type UpdateCustomerBody =
  paths['/customers/{id}']['patch']['requestBody']['content']['application/json']
export type CreatePaymentBody =
  paths['/payments']['post']['requestBody']['content']['application/json']

// === Responses
export type AuthLoginResponse =
  paths['/auth/login']['post']['responses']['200']['content']['application/json']
export type VerifyEmailResponse =
  paths['/auth/verify-email']['post']['responses']['200']['content']['application/json']
export type RegisterResponse =
  paths['/auth/register']['post']['responses']['200']['content']['application/json']
export type AuthMeResponse =
  paths['/auth/me']['get']['responses']['200']['content']['application/json']
export type OkResponse =
  paths['/auth/change-password']['patch']['responses']['200']['content']['application/json'] // { ok: true }
export type PasswordResetRequestResponse =
  paths['/auth/forgot-password']['post']['responses']['200']['content']['application/json'] // { ok: true, token?: string }
export type BillResponse =
  paths['/bills']['post']['responses']['201']['content']['application/json']

// === État d’auth côté front
export type AuthState = AuthLoginResponse // { token, user: { id, email, name, role, ownerId } }

export type ListCustomersParams = Partial<{
  q: string
  take: number // 1..100
  cursor: string
  order: 'asc' | 'desc'
}>

export type PaymentsByBillResponse = {
  bill: Pick<Bill, 'id' | 'status' | 'total' | 'customer'>
  payments: BillPayment[]
  paid: number
  remaining: number
}

export type GenericPayment = {
  id: string
  customerId: string
  billId: string | null
  amount: number
  method: PaymentMethod
  incurredOn: string
  note?: string
  reference?: string
}

export type PaymentsByCustomerResponse = {
  customer: { id: string; name: string }
  total: number
  payments: GenericPayment[]
}

export type PaymentDetailResponse = GenericPayment & {
  bill?: Pick<Bill, 'id' | 'total' | 'status'>
  customer?: { id: string; name: string }
}

export const BUCKET_ORDER = [
  'Current',
  '1-30',
  '31-60',
  '61-90',
  '90+',
] as const
export type BucketLabel = (typeof BUCKET_ORDER)[number]

export type AgingBucket = {
  label: BucketLabel
  fromDays: number
  toDays?: number
  total: number
}

export type AgingCustomerOpen = {
  customerId: string
  name: string
  open: number
}

export type BillsAgingResponse = {
  asOf: string
  terms: number
  grandTotalBrut: number
  creditsUnapplied: number
  grandTotalNet: number
  buckets: AgingBucket[]
  customers?: AgingCustomerOpen[]
}

export interface XYPoint {
  x: string
  y: number
}
export interface BarDatumNV {
  name: string
  value: number
  [key: string]: string | number
}
export interface PieDatumNV {
  id: string
  label: string
  value: number
}

export type AgingBarDatum = {
  tranche: string
} & Partial<Record<BucketLabel, number>>

// ---- Expenses ----
export interface Expense {
  id: string
  name: string
  amount: number
  incurredOn: string // ISO
  categoryId?: string | null
  vendorId?: string | null
  note?: string | null
  attachments?: unknown | null
  createdAt: string
  updatedAt: string
  deletedAt?: string | null
  category?: { id: string; name: string } | null
  vendor?: { id: string; name: string } | null
}

export interface ExpensesSummary {
  totals: { count: number; amount: number }
  monthly: { ym: string; total: number }[]
  byCategory: { name: string | null; total: number }[]
  byVendor: { name: string | null; total: number }[]
}

// ---- Cash ----
export interface CashSummary {
  totals: {
    in: number
    outRefunds: number // remboursements (négatif)
    outExpenses: number // dépenses (négatif)
    out: number // outRefunds + outExpenses
    net: number // in + out
  }
  monthly: {
    ym: string
    in: number
    outRefunds: number
    outExpenses: number
    out: number
    net: number
  }[]
}

// ---- Payments summary (typed) ----
export interface PaymentsSummary {
  totals: { in: number; out: number; net: number }
  byMethod: { method: string; total: number }[]
  daily: { day: string; total: number }[]
  monthly: { ym: string; in: number; out: number; net: number }[]
  byCustomer: { customerId: string; name: string; total: number }[]
}

export type ExpenseView = {
  id: string
  name: string
  amount: number
  incurredOn: string
  note?: string | null
  category?: { id: string; name: string } | null
  vendor?: { id: string; name: string } | null
}

export type ExpensesListResponse = {
  data: ExpenseView[]
  total: number
  page: number
  pageSize: number
}

export type CreateExpenseBody = {
  name: string
  amount: number
  incurredOn: string // 'YYYY-MM-DD'
  categoryId?: string
  vendorId?: string
  note?: string
}
export type UpdateExpenseBody = Partial<CreateExpenseBody>

export type ExpensesSummaryResponse = {
  totals: { count: number; amount: number }
  monthly: { ym: string; total: number }[]
  byCategory: { name: string | null; total: number }[]
  byVendor: { name: string | null; total: number }[]
}

export type CashSummaryResponse = {
  totals: {
    in: number
    outRefunds: number
    outExpenses: number
    out: number
    net: number
  }
  monthly: {
    ym: string
    in: number
    outRefunds: number
    outExpenses: number
    out: number
    net: number
  }[]
}

// src/types/users.ts
export type Role = 'OWNER' | 'ADMIN' | 'STAFF'

export interface UserRow {
  id: string
  email: string
  name: string
  role: Role
  active: boolean
  createdAt?: string
  lastLoginAt?: string
}

export interface CreateUserBody {
  name: string
  email: string
  password: string
  role?: Extract<Role, 'ADMIN' | 'STAFF'>
}

export interface UpdateUserBody {
  email?: string
  name?: string
  password?: string
  role?: Extract<Role, 'ADMIN' | 'STAFF'>
  active?: boolean
}

export type TransferBody = {
  itemId: string
  quantity: number
  fromContainerId: string
  toContainerId: string
  note?: string
  occurredAt?: string
}

export type Container = {
  id: string
  ownerId: string
  depotId: string
  name: string
  code: string
  isDefault: boolean
  description?: string | null
  createdAt: string
  updatedAt?: string
}

export type ContainerSelect = {
  id: string
  name: string
  depotId: string
  isDefault: boolean
}

export type ContainerDetail = Container & {
  depot: { id: string; name: string; code?: string }
  inventoryLevels: {
    qty: number
    item: { id: string; name: string; sku?: string | null }
  }[]
}

export type ContainerList = Container[]

export type Depot = {
  id: string
  ownerId: string
  name: string
  address?: string | null
  code: string
  isDefault: boolean
  createdAt: string
  updatedAt?: string
}

export type DepotSelect = {
  id: string
  name: string
}

export type DepotDetail = Depot & {
  containers: Container[]
}

export type DepotList = Depot[]

export type AdjustContainerStockBody = {
  itemId: string
  quantity: number // positif = Stocker, négatif = Destocker
  unitPrice: string // ex "1000.00"
  occurredAt?: string // ISO string optionnel
  note?: string
}

// ===== Types de réponses =====
export type ContainerMoveRow = {
  id: string
  name: 'Stocker' | 'Destocker'
  quantity: number
  category: string | null
  createdAt: string
}
export type ContainerMovesResponse = {
  products: ContainerMoveRow[]
  countProductTotalByContainer: number
  compte: Array<{ _id: string; totalQuantity: number }>
}

export type ContainerInventoryRow = {
  ownerId: string
  itemId: string
  containerId: string
  qty: number
  item: { id: string; name: string; sku: string | null }
}
export type ContainerInventoryResponse = {
  total: number
  rows: ContainerInventoryRow[]
}

export type DepotMoveRow = {
  id: string
  name: 'Stocker' | 'Destocker'
  quantity: number
  category: string | null
  createdAt: string
  restQuantity: number
}
export type DepotMovesResponse = {
  products: DepotMoveRow[]
  countProductTotalByDepot: number
  compte: Array<{ _id: string; totalQuantity: number }>
}

export type InvoicePrintFormat = 'A4' | 'THERMAL_80' | 'THERMAL_58'

export type InvoiceSettings = {
  printFormat: InvoicePrintFormat
  businessName: string
  businessPhone: string
  businessAddress: string
  logoUrl: string
}

export type OwnerSettings = {
  digestEnabled?: boolean
  digestHour?: string
  tz?: string
  dunningEnabled?: boolean
  invoice?: InvoiceSettings
}

export type OwnerSettingsResponse = { settings: OwnerSettings }

export type OwnerTrialView = {
  id: string
  name: string
  plan: 'MONTHLY_STANDARD' | 'YEARLY_ECO' | 'LIFETIME_PREMIUM'
  planStartedAt: string | null
  nextRenewalAt?: string | null
  trialStartedAt: string | null
  trialEndsAt: string | null
  trialIncluded: number
  suspendedAt: Date | null
  isActive: boolean
  suspensionReason: string | null
  messageUsage?: { included: number; used: number } | null
  now?: string
}

export type OwnerSummary = {
  ownerId: string
  planStartedAt: string | null
  trial: {
    state: 'NO_TRIAL' | 'ACTIVE' | 'EXPIRED'
    daysLeft?: number
    daysSince?: number
  }

  canInvite: boolean
  canSendWhatsApp: boolean
}

// --- ENTITLEMENTS ---
export type MessageChannel = 'WHATSAPP' | 'SMS'

type MessageUsageView = {
  planIncluded: number
  topupIncluded: number
  totalIncluded: number
  used: number
  remaining: number
  overageCfa: number
}

export type EntitlementsResponse = {
  ok: boolean
  account: {
    isActive: boolean
    suspendedAt: string | null
    suspensionReason: string | null
  }
  plan: {
    code: 'MONTHLY_STANDARD' | 'YEARLY_ECO' | 'LIFETIME_PREMIUM'
    active: boolean
    nextRenewalAt: string | null
  }
  storage: {
    used: string
    max: string
  }
  messages: {
    periodKey: string
    includedByPlan: {
      SMS: number
      EMAIL: number
      WHATSAPP: number
    }
    byChannel: {
      SMS: MessageUsageView
      EMAIL: MessageUsageView
      WHATSAPP: MessageUsageView
    }
  }
  canWrite: boolean
}

// --- DUNNING RULES ---
export type DunningRule = {
  id: string
  ownerId: string
  name: string
  enabled: boolean
  channel: 'EMAIL' | 'WHATSAPP'
  template: 'DEFAULT' | 'SOFT' | 'HARD'
  runHour: string
  tz?: string | null
  scope: 'ALL' | 'OPTIN' | 'BY_IDS'
  customerIds?: string[] | null
  minDebtCfa: number
  maxPerRun: number
  lastRunAt?: string | null
  createdAt: string
  updatedAt: string
  frequencyDays?: number
}

export type DunningPreviewRow = {
  id: string
  name: string
  contact?: string | null
  phone?: string | null
  email?: string | null
  whatsappOptIn: boolean
  debt: number
}

// --- DUNNING remind one ---
export type RemindCustomerBody = {
  customerId: string
  channel: 'SMS' | 'EMAIL' | 'WHATSAPP'
  template?: 'DEFAULT' | 'SOFT' | 'HARD'
}

export type ItemImportMode = 'CREATE_ONLY' | 'UPSERT_BY_SKU'

export type ItemImportColumns = {
  name?: string
  sku?: string
  unitPrice?: string
  category?: string
  packaging?: string
}

export type ItemImportOptions = {
  delimiter?: string
  updateIfExists?: boolean
  createCategories?: boolean
  mode?: ItemImportMode
  columns?: ItemImportColumns
}

export type ItemImportPreviewRowOk = {
  index: number
  name: string
  sku: string | null
  unitPrice: number
  categoryName?: string
  packaging?: string | null
}

export type ItemImportPreviewRowErr = {
  index: number
  message: string
  raw?: any
}

export type ItemImportPreview = {
  mode: ItemImportMode
  summary: {
    total: number
    valid: number
    invalid: number
    willCreate: number
    willUpdate: number
    willSkip?: number
  }
  ok: ItemImportPreviewRowOk[]
  errors: ItemImportPreviewRowErr[]
  notes?: string[]
}

export type ItemImportCommitResult = {
  ok: true
  mode: ItemImportMode
  created: number
  updated: number
  skipped?: number
  total: number
}

// =======================
// INVENTORY / STOCK IMPORT
// =======================

export type InventoryImportMode = 'SET' | 'ADD'

export type InventoryImportColumns = {
  /** identifiant produit */
  sku?: string // default: "sku"
  name?: string // default: "name" (si MATCH_BY_NAME)
  /** quantité à importer */
  qty?: string // default: "qty"
  /** coût unitaire (optionnel) -> si absent, backend peut fallback sur item.unitPrice */
  unitCost?: string // default: "unitCost"
  /** conteneur (optionnel) -> peut override le containerId global */
  containerCode?: string // default: "containerCode"
  containerId?: string // default: "containerId"
}

export type InventoryImportOptions = {
  delimiter?: string // ";" "," etc.
  mode?: InventoryImportMode
  /** conteneur global si non fourni ligne par ligne */
  containerId?: string
  /** si true, le backend crée les expenses liées aux achats; pour stock initial => false */
  autoExpense?: boolean
  /** mapping de colonnes si le CSV n'a pas les headers standards */
  columns?: InventoryImportColumns
}

/** Une ligne validée du preview (après mapping/validation) */
export type InventoryImportRowOk = {
  index: number // numéro de ligne CSV (ex: i+2)
  itemId?: string // si le backend a résolu le produit au preview
  sku?: string | null
  name?: string
  qty: number
  unitCost?: number | null
  /** conteneur résolu (si fourni) */
  containerId?: string | null
  containerCode?: string | null
}

/** Une erreur ligne (ou globale index=0) */
export type InventoryImportRowErr = {
  index: number
  message: string
  raw?: any
}

export type InventoryImportPreviewSummary = {
  unresolvedItemsBySku: number
  willCreate: any
  willUpdate: any
  total: number
  valid: number
  invalid: number
  /** nombre de mouvements/écritures qui seront créés */
  willCreateMoves: number
  /** nombre d’items non trouvés (si tu veux le compter) */
  notFound?: number
}

export type InventoryImportPreview = {
  mode: InventoryImportMode
  summary: InventoryImportPreviewSummary
  ok: InventoryImportRowOk[] // limité à 200 par ex
  errors: InventoryImportRowErr[] // limité à 200
  notes?: string[]
  /** fingerprint optionnel si tu veux cacher la preview */
  fingerprint?: string
}

export type InventoryImportCommitResult = {
  adjusted: number
  total: any
  created: any
  updated: any
  ok: true
  mode: InventoryImportMode
  createdMoves: number
  /** combien de lignes traitées (validées) */
  processed: number
  /** optionnel: si tu fais du "set qty" par inventaire */
  updatedLevels?: number
}

export type InventoryLevelRow = {
  itemId: string
  itemName: string
  sku?: string | null
  containerId: string
  containerName: string
  qty: number
}

export type InventoryLevelsList = {
  data: InventoryLevelRow[]
  total: number
  page: number
  pageSize: number
}

export type InventoryLevelsQuery = {
  search?: string
  containerId?: string
  page?: number
  pageSize?: number
}

export type OwnerPlanStatus = 'ACTIVE' | 'EXPIRED' | 'TRIAL' | 'INACTIVE'
export type OwnerMessagingStatus =
  | 'ACTIVE'
  | 'SUSPENDED'
  | 'EXPIRED'
  | 'NOT_PAID'

export type OwnerBillingOverviewResponse = {
  ok: true
  account: {
    isActive: boolean
    suspendedAt: string | null
    suspensionReason: string | null
  }
  plan: {
    code: 'MONTHLY_STANDARD' | 'YEARLY_ECO' | 'LIFETIME_PREMIUM'
    startedAt: string | null
    nextRenewalAt: string | null
    status: OwnerPlanStatus
  }
  messaging: {
    plan: 'MONTHLY' | 'YEARLY' | null
    startedAt: string | null
    nextRenewalAt: string | null
    suspendedAt: string | null
    status: OwnerMessagingStatus
  }
  storage: {
    used: string
    max: string
  }
  messages: {
    periodKey: string
    byChannel: {
      EMAIL: {
        planIncluded: number
        topupIncluded: number
        totalIncluded: number
        used: number
        remaining: number
        overageCfa: number
      }
      SMS: {
        planIncluded: number
        topupIncluded: number
        totalIncluded: number
        used: number
        remaining: number
        overageCfa: number
      }
      WHATSAPP: {
        planIncluded: number
        topupIncluded: number
        totalIncluded: number
        used: number
        remaining: number
        overageCfa: number
      }
    }
  }
}

export type OwnerSubscriptionPaymentRow = {
  id: string
  plan: 'MONTHLY_STANDARD' | 'YEARLY_ECO' | 'LIFETIME_PREMIUM'
  amountCfa: number
  currency: string
  status: 'PENDING' | 'SUCCEEDED' | 'FAILED' | 'CANCELLED'
  paidAt: string | null
  createdAt: string
  waveCheckoutUrl?: string | null
  waveSessionId?: string | null
  wavePaymentId?: string | null
  errorCode?: string | null
  errorMessage?: string | null
}

export type OwnerSubscriptionPaymentsResponse = {
  ok: true
  items: OwnerSubscriptionPaymentRow[]
}

export type OwnerMessagingSubscriptionPaymentRow = {
  id: string
  plan: 'MONTHLY' | 'YEARLY'
  amountCfa: number | null
  currency: string
  status: 'PENDING' | 'SUCCEEDED' | 'FAILED' | 'CANCELLED'
  paidAt: string | null
  createdAt: string
  waveCheckoutUrl?: string | null
  waveSessionId?: string | null
  wavePaymentId?: string | null
  errorCode?: string | null
  errorMessage?: string | null
}

export type OwnerMessagingSubscriptionPaymentsResponse = {
  ok: true
  items: OwnerMessagingSubscriptionPaymentRow[]
}

export type OwnerMessageCreditRow = {
  id: string
  channel: 'WHATSAPP' | 'SMS' | 'EMAIL'
  periodKey: string
  qty: number
  amountCfa: number | null
  currency: string | null
  source: 'PACK' | 'MANUAL' | 'BONUS' | 'ADJUSTMENT' | 'WAVE'
  note: string | null
  meta: unknown
  createdAt: string
  packId: string | null
  packPurchaseId: string | null
  pack: {
    id: string
    code: string
    name: string
    messages: number
    priceCfa: number
  } | null
  packPurchase: {
    id: string
    status: 'PENDING' | 'SUCCEEDED' | 'FAILED' | 'CANCELLED'
    paidAt: string | null
    amountCfa: number
    currency: string
  } | null
  createdByAdmin: {
    id: string
    email: string
    name: string
    role: 'SUPER_ADMIN' | 'SUPPORT' | 'BILLING' | 'OPS'
  } | null
}

export type OwnerMessageCreditsResponse = {
  ok: true
  items: OwnerMessageCreditRow[]
}

export type OwnerMessagePurchaseRow = {
  id: string
  amountCfa: number
  currency: string
  status: 'PENDING' | 'SUCCEEDED' | 'FAILED' | 'CANCELLED'
  paidAt: string | null
  createdAt: string
  errorCode?: string | null
  errorMessage?: string | null
  pack: {
    code: string
    name: string
    messages: number
    priceCfa: number
  }
}

export type OwnerMessagePurchasesResponse = {
  ok: true
  items: OwnerMessagePurchaseRow[]
}
